/**
 * Personal keys and sharing encryption keys with members.
 *
 * Each person can create a key pair in this browser; the private key is stored on the server only
 * wrapped by their own passphrase (and optionally a recovery key). Sharing an encrypted notebook,
 * project or note with someone wraps its key for their public key, so they open it with their own
 * passphrase. The key itself never leaves the browser unwrapped.
 */
import { useQuery } from "@tanstack/react-query";
import {
  KDF_ITERATIONS,
  generateScopeKey,
  generateUserKeyPair,
  importUserPrivateKey,
  keyProof,
  publicKeyFingerprint,
  userKeyProof,
  wrapScopeKeyForMember,
  wrapUserPrivateKey,
  type MemberSlotData,
} from "@brainlog/shared";
import { get, post, put } from "@/lib/api";
import { getScopeKey, rememberPersonalKey, type PersonalKeyRecord } from "./keyring";
import type { NewScope } from "./convert";

export const personalKeyQuery = ["personal-key"] as const;

export function usePersonalKeyRecord(enabled = true) {
  return useQuery({
    queryKey: personalKeyQuery,
    queryFn: () => get<{ key: PersonalKeyRecord | null }>("/api/me/encryption-key").then((r) => r.key),
    enabled,
    staleTime: 60_000,
  });
}

export function useFingerprint(publicKey: string | null | undefined) {
  return useQuery({ queryKey: ["fingerprint", publicKey], queryFn: () => publicKeyFingerprint(publicKey!), enabled: !!publicKey, staleTime: Infinity });
}

/**
 * Create (or, with `replace`, start over with) the personal key. The new key is unlocked in this tab.
 */
export async function createPersonalKey(userId: string, passphrase: string, recoveryKey: string | null, replace = false) {
  const pair = await generateUserKeyPair();
  const slots = [await wrapUserPrivateKey(pair.pkcs8, passphrase, userId, "passphrase", KDF_ITERATIONS)];
  if (recoveryKey) slots.push(await wrapUserPrivateKey(pair.pkcs8, recoveryKey, userId, "recovery", KDF_ITERATIONS));
  await post("/api/me/encryption-key", { publicKey: pair.publicKey, keyProof: await userKeyProof(pair.pkcs8, userId), slots, replace });
  await rememberPersonalKey(userId, pair.pkcs8, pair.publicKey, await importUserPrivateKey(pair.pkcs8));
}

/** Re-wrap the unlocked personal key with a new passphrase or recovery key. */
export async function changePersonalSecret(userId: string, pkcs8: Uint8Array, secret: string, kind: "passphrase" | "recovery") {
  const slot = await wrapUserPrivateKey(pkcs8, secret, userId, kind, KDF_ITERATIONS);
  await put("/api/me/encryption-key/slots", { keyProof: await userKeyProof(pkcs8, userId), slots: [slot] });
}

export interface AccessMember {
  id: string;
  displayName: string;
  role: string;
  publicKey: string | null;
  hasKey: boolean;
}

export const accessQuery = (scopeId: string) => ["scope-access", scopeId] as const;

/** Members of the key's notebook/project: whether they can open it, and whether they could be given access. */
export function useScopeAccess(scopeId: string | null | undefined) {
  return useQuery({
    queryKey: accessQuery(scopeId ?? ""),
    queryFn: () => get<{ members: AccessMember[] }>(`/api/encryption/scopes/${scopeId}/access`).then((r) => r.members),
    enabled: !!scopeId,
  });
}

async function wrapFor(raw: Uint8Array, scopeId: string, recipients: { id: string; publicKey: string }[]): Promise<MemberSlotData[]> {
  return Promise.all(recipients.map((r) => wrapScopeKeyForMember(raw, scopeId, r.id, r.publicKey)));
}

/**
 * Give members access to an unlocked key. Only people with a personal key can receive it; the
 * returned list says who was given access.
 */
export async function shareScopeKey(scopeId: string, recipients: { id: string; publicKey: string | null }[]) {
  const k = getScopeKey(scopeId);
  if (!k) throw new Error("Unlock it first: only someone who can open it can share its key.");
  const withKeys = recipients.filter((r): r is { id: string; publicKey: string } => !!r.publicKey);
  if (!withKeys.length) return [];
  await put(`/api/encryption/scopes/${scopeId}/members`, { keyProof: k.proof, slots: await wrapFor(k.raw, scopeId, withKeys) });
  return withKeys.map((r) => r.id);
}

/** A fresh key opened by personal keys only: the creator's and the members' who have one. */
export async function buildPersonalScope(recipients: { id: string; publicKey: string }[]): Promise<NewScope> {
  const id = crypto.randomUUID();
  const { raw } = await generateScopeKey();
  return { id, raw, keyProof: await keyProof(raw, id), slots: [], memberSlots: await wrapFor(raw, id, recipients) };
}

/** Public keys of the members of a notebook or project (null for people without a personal key). */
export async function memberPublicKeys(workspaceId: string) {
  const ws = await get<{ members: { id: string; displayName: string; role: string }[] }>(`/api/workspaces/${workspaceId}`);
  return Promise.all(
    ws.members.map(async (m) => ({ ...m, publicKey: (await get<{ key: { publicKey: string } | null }>(`/api/users/${m.id}/encryption-key`)).key?.publicKey ?? null })),
  );
}
